import * as React from 'react';
import withStyles from 'react-jss';
import { Button, DatePicker, Drawer, Dropdown, Menu, message, Select, Table, TimePicker, Input, InputNumber } from 'antd';
import { CssType, ThemeType } from 'src/theme/jss-types';
import { CloseOutlined, DownOutlined, UploadOutlined, EyeOutlined, EditOutlined } from '@ant-design/icons';
import { HocOptions } from 'src/common/generic-hoc.types';
import GenericHoc from 'src/common/generic-hoc';
import { BulkActionsMenu, PieceDetailsColumnList, PieceStatusOptions, StatusOptions, uploadPodFiles, getFailureReasonList } from './DeliveryAcknowledgement.utils';
import PodUpload from 'src/common/PodUpload';
import PodImagePreviewModal from 'src/common/PodImagePreviewModal';
import NdrNprReasonModal from './NdrNprReasonModal';
import { markCNsAsDelivered, setPieceLevelActions, setEventTimestamp, markCNsAsPickedUp } from './DeliveryAcknowledgement.actionsCreator';
import moment from 'moment';
import { EditPieceDetailsSideDrawerStyles } from './EditPieceDetailsSideDrawer.styles';
import { validateInvoicePOD } from '../Invoices/Invoices.api';
import InvoiceUploadErrorModal from '../Invoices/InvoiceUploadErrorModal';
import { getRequiredInvoiceConfig } from '../Invoices/Invoices.utils';
import { useTranslation } from 'react-i18next';

interface EditPieceDetailsSideDrawerProps {
  classes: any;
  isVisible: boolean;
  handleClose: () => void;
  consignmentData: any;
  deliveryAcknowledgement: any;
  setPieceLevelActions: (data) => void;
  markCNsAsDelivered: () => any;
  markCNsAsPickedUp: () => any;
  setEventTimestamp: (data) => void;
  actionType: string;
  consignmentActionConfig: any;
  separateChildCNs?: boolean;
  allFailureReasons?: Array<{
    id: string;
    name: string;
    is_ndr: boolean;
    is_npr: boolean;
  }>;
}

const EditPieceDetailsSideDrawer = (props: EditPieceDetailsSideDrawerProps) => {
  const {
    classes,
    isVisible,
    handleClose,
    consignmentData,
    deliveryAcknowledgement,
    setPieceLevelActions,
    markCNsAsDelivered,
    markCNsAsPickedUp,
    setEventTimestamp,
    allFailureReasons,
    separateChildCNs = false,
    actionType,
    consignmentActionConfig,
  } = props;
  const { t } = useTranslation();

  const { pieceLevelActions = {}, loader } = deliveryAcknowledgement || {};
  const [selectedRows, setSelectedRows] = React.useState<string[]>([]);
  const [podUploadModalParams, setPODUploadModalParams] = React.useState<{
    isVisible: boolean,
    referenceNumber?: string,
    isUploading?: boolean,
    uploadedImageList?: any[],
    pieceStatusData: any,
  }>({
    isVisible: false,
    pieceStatusData: {},
  });
  const [imageModalPreviewProps, setImageModalPreviewProps] = React.useState<{
    isVisible: boolean,
    imageList: Record<string, any>[],
  }>({
    isVisible: false,
    imageList: [],
  });
  const [errorModalProps, setErrorModalProps] = React.useState<{
    isVisible: boolean,
    failureReason: string,
  }>({
    isVisible: false,
    failureReason: '',
  });
  const [ndrNprModalProps, setNdrNprModalProps] = React.useState<{
    isVisible: boolean,
    selectedStatus: string,
  }>({
    isVisible: false,
    selectedStatus: '',
  });

  const proofImageListKey = actionType === 'delivery' ? 'image_url_list' : 'pop_image_list';

  const isPODForConsignment = () => {
    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      {
        vendorData: {
          vendor_category: consignmentData?.vendor_category,
        },
      },
    );
    return requiredConfig?.pod_for_consignment === true;
  };

  const getMinPODImages = (record) => {
    const recordObj = pieceLevelActions?.[record.reference_number];
    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      { consignmentData },
    );

    return requiredConfig?.[recordObj?.status]?.min_proof_count ?? 1;
  };

  const getActionConfig = () => {
    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      { consignmentData },
    );
    const statusKey = actionType === 'delivery' ? 'delivered' : 'pickup_completed';
    return requiredConfig?.[statusKey] || {};
  };

  const actionConfig = getActionConfig();
  const hideTimestamp = actionConfig?.hide_timestamp === true;

  const prefillData = () => {
    const pieceLevelActionsData = {};
    consignmentData?.pieces_detail?.forEach((item) => {
      const status = item?.['_internal']?.status || item?.status;
      const quantity = item?.quantity || 0;
      pieceLevelActionsData[item.reference_number] = {
        status: (
          status &&
          PieceStatusOptions[actionType]?.some((item) => item.key === status)
        ) ? status : undefined,
        pod: item?.['_internal']?.[proofImageListKey],
        quantity_delivered: item?.['_internal']?.quantity_delivered || item?.quantity_delivered || quantity || 0,
        quantity_pickedup: item?.['_internal']?.quantity_pickedup || item?.quantity_pickedup || quantity || 0,
      };
    });
    setPieceLevelActions(pieceLevelActionsData);
    setEventTimestamp({ date: null, time: null });
    if (consignmentData?.delivery_completion_time) {
      setEventTimestamp({
        date: moment(consignmentData.delivery_completion_time)?.format('YYYY-MM-DD'),
        time: moment(consignmentData.delivery_completion_time)?.format('HH:mm'),
      });
    }
  };

  React.useEffect(() => {
    prefillData();
  }, []);

  const handlePODValidation = async (files) => {
    if (!files?.length) {
      message.error(t('Please select a file to upload'));
      return;
    }
    setPODUploadModalParams({
      ...podUploadModalParams,
      isUploading: true,
    });

    const existingPodImages = [];
    const newPodImages = [];
    let newPodImageUrls = [];

    files?.forEach((file) => {
      if (file.url) {
        existingPodImages.push({
          url: file.url,
          type: file.type,
        });
      } else {
        newPodImages.push(file);
      }
    });

    if (newPodImages.length) {
      newPodImageUrls = await uploadPodFiles(newPodImages);
    }

    const pieceStatus = podUploadModalParams?.pieceStatusData?.status;
    const pickupAction = ['pickup_completed', 'not_picked_up'];

    const response = await validateInvoicePOD({
      actionType: pickupAction.includes(pieceStatus) ? 'pickup' : 'delivery',
      status: pieceStatus,
      consignment_reference_number: consignmentData.reference_number,
      reference_number: podUploadModalParams.referenceNumber,
      proof_image_list: [...(existingPodImages || []), ...(newPodImageUrls || [])],
    });

    if (response?.isSuccess) {
      if (response?.data?.failureArray?.length) {
        setPODUploadModalParams({
          ...podUploadModalParams,
          uploadedImageList: [...(existingPodImages || []), ...(newPodImageUrls || [])],
          isUploading: false,
        });
        setErrorModalProps({
          isVisible: true,
          failureReason: response.data.failureArray[0].failure_reason,
        });
      } else {
        const finalPodImages = [...(existingPodImages || []), ...(newPodImageUrls || [])];
        if (isPODForConsignment()) {
          const updatedPieceActions = { ...pieceLevelActions };
          consignmentData?.pieces_detail?.forEach((piece) => {
            updatedPieceActions[piece.reference_number] = {
              ...pieceLevelActions[piece.reference_number],
              pod: finalPodImages,
            };
          });
          setPieceLevelActions(updatedPieceActions);
        } else {
          setPieceLevelActions({
            ...pieceLevelActions,
            [podUploadModalParams.referenceNumber]: {
              ...pieceLevelActions[podUploadModalParams.referenceNumber],
              pod: finalPodImages,
            },
          });
        }

        setPODUploadModalParams({
          isVisible: false,
          isUploading: false,
          pieceStatusData: {},
        });
      }
    } else {
      message.error(response.errorMessage);
      setPODUploadModalParams({
        ...podUploadModalParams,
        isUploading: false,
      });
    }
  };

  const renderImageModal = () => {
    if (!imageModalPreviewProps.imageList?.length) {
      return null;
    }
    return (
      <PodImagePreviewModal
        isVisible={imageModalPreviewProps.isVisible}
        handleClose={() => setImageModalPreviewProps({
          isVisible: false,
          imageList: [],
        })}
        imageList={imageModalPreviewProps.imageList}
      />
    );
  };

  const isDelivery = actionType === 'delivery';
  const isDisabled = ['delivered', 'attempted'].includes(consignmentData.status);

  const handleUpdate = async () => {
    const requiredConfig = getRequiredInvoiceConfig(
      consignmentActionConfig?.flow_config,
      { consignmentData },
    );

    if (!hideTimestamp && (
      !deliveryAcknowledgement.eventTimestamp?.date ||
      !deliveryAcknowledgement.eventTimestamp?.time
    )) {
      message.error(t(`Please select ${isDelivery ? 'delivery' : 'Pickup'} timestamp`));
      return;
    }

    if (consignmentData?.pieces_detail?.some((item) =>
      !pieceLevelActions[item.reference_number]?.status)
    ) {
      message.error(t('Please select status for all pieces'));
      return;
    }

    if (actionType === 'pickup' && Object.keys(pieceLevelActions || {})?.every((key) =>
      pieceLevelActions[key]?.status !== 'pickup_completed')) {
      message.error(t('Please mark at least one piece as picked up'));
      return;
    }

    if (isDelivery && Object.keys(pieceLevelActions || {})?.every((key) =>
      pieceLevelActions[key]?.status !== 'delivered')) {
      message.error(t('Please mark at least one piece as delivered'));
      return;
    }

    const piecesWithoutPOD = [];
    Object.keys(pieceLevelActions || {}).forEach((key) => {
      const pieceData = pieceLevelActions[key];
      if (requiredConfig?.[pieceData?.status]?.pod_required && !pieceData?.pod?.length) {
        piecesWithoutPOD.push(key);
      }
    });

    if (piecesWithoutPOD.length > 0) {
      const errorMessage = t('Please upload POD for pieces');
      message.error(`${errorMessage}: ${piecesWithoutPOD.join(', ')}`);
      return;
    }
    // Validate failure reasons for partial deliveries/pickups
    const availableReasons = getFailureReasonList(allFailureReasons, actionType);
    const hasReasons = Array.isArray(availableReasons) && availableReasons.length;

    if (hasReasons) {
      const invalidPieces = (consignmentData?.pieces_detail || []).filter((piece) => {
        const pieceData = pieceLevelActions[piece.reference_number];
        if (!pieceData?.status) return false;

        // Check if the piece has a failure reason
        // need to ask reason if it's a failure or partial delivery/pickup
        const isFailure = (actionType === 'delivery' && pieceData.status === 'attempted') ||
                         (actionType === 'pickup' && pieceData.status === 'not_picked_up');

        const isPartialDelivery = actionType === 'delivery' &&
          pieceData.status === 'delivered' &&
          pieceData.quantity_delivered < piece.quantity;

        const isPartialPickup = actionType === 'pickup' &&
          pieceData.status === 'pickup_completed' &&
          pieceData.quantity_pickedup < piece.quantity;

        return (isFailure || isPartialDelivery || isPartialPickup) && !pieceData.failure_reason;
      });

      if (invalidPieces.length > 0) {
        message.error(t('Please select a reason for all pieces with partial quantities or failures'));
        return;
      }
    }

    const response = isDelivery ? await markCNsAsDelivered() : await markCNsAsPickedUp();

    if (response.isSuccess) {
      handleClose();
    }
  };

  const renderTitle = () => {
    return (
      <div className={classes.justifyContent}>
        <div className={classes.flexCenter}>
          <CloseOutlined
            className={classes.closeButton}
            onClick={() => handleClose()}
          />
          <div style={{ color: '#111111', fontWeight: 600 }}>{t('Piece Details')}</div>
        </div>
        <div className={classes.flexCenter}>
          <div
            className={classes.cancelButton}
            onClick={() => handleClose()}
          >
            {t('Cancel')}
          </div>
          <Button
            className={isDisabled ? classes.updateButtonDisabled : classes.updateButton}
            onClick={handleUpdate}
            loading={loader}
            disabled={isDisabled}
          >
            {t('Update')}
          </Button>
        </div>
      </div>
    );
  };

  const rowSelection = {
    onChange: (selectedRowKeys: string[]) => {
      setSelectedRows(selectedRowKeys);
    },
    getCheckboxProps: (record) => ({
      disabled: ['delivered', 'attempted'].includes(record.status),
    }),
  };

  const handleStatusChange = (value, record) => {
    const pieceCurrentData = pieceLevelActions?.[record.reference_number] || {};
    setPieceLevelActions({
      ...pieceLevelActions,
      [record.reference_number]: {
        ...pieceCurrentData,
        status: value,
        quantity_pickedup: ['not_picked_up'].includes(value) ? 0 : pieceCurrentData?.quantity_pickedup || pieceCurrentData?.quantity,
        quantity_delivered: ['attempted'].includes(value) ? 0 : pieceCurrentData?.quantity_delivered || pieceCurrentData?.quantity,
      },
    });
  };

  const handleReasonChange = (value, record) => {
    const selectedReason = getFailureReasonList(allFailureReasons, actionType)
      .find((r) => r.name === value);
    setPieceLevelActions({
      ...pieceLevelActions,
      [record.reference_number]: {
        ...pieceLevelActions[record.reference_number],
        failure_reason: selectedReason?.name,
        failure_reason_id: selectedReason?.id,
      },
    });
  };

  const handleReasonCommentChange = (e, record) => {
    const val = e?.target?.value;
    setPieceLevelActions({
      ...pieceLevelActions,
      [record.reference_number]: {
        ...pieceLevelActions[record.reference_number],
        comment: val,
      },
    });
  };

  const renderStatusDropdown = (record) => {
    const pieceData = pieceLevelActions?.[record.reference_number] || {};
    const availableReasons = getFailureReasonList(allFailureReasons, actionType);

    const isPieceActionFailure =
      (actionType === 'delivery' && pieceData.status === 'attempted') ||
      (actionType === 'pickup' && pieceData.status === 'not_picked_up');

    // Check if quantities are less than total (partial delivery/pickup)
    const isPartialDelivery = actionType === 'delivery' &&
      pieceData.status === 'delivered' &&
      pieceData.quantity_delivered < record.quantity;

    const isPartialPickup = actionType === 'pickup' &&
      pieceData.status === 'pickup_completed' &&
      pieceData.quantity_pickedup < record.quantity;

    const shouldDisplayReason =
      (isPieceActionFailure || isPartialDelivery || isPartialPickup) &&
      Array.isArray(availableReasons) && availableReasons.length > 0;

    return (
      <div className={classes.statusDropdownContainer}>
        <Select
          style={{ width: 160 }}
          value={pieceData?.status}
          placeholder={t('Select')}
          onChange={(value) => handleStatusChange(value, record)}
          dropdownMatchSelectWidth={false}
        >
          {StatusOptions[actionType]?.map((item) => (
            <Select.Option key={item.key} value={item.key}>
              {t(item.label)}
            </Select.Option>
          ))}
        </Select>

        {(isPieceActionFailure || isPartialDelivery || isPartialPickup) && (
          <div className={classes.reasonSection}>
            {shouldDisplayReason && (
              <div className={classes.reasonDropdownContainer}>
                <label className={classes.reasonLabel}>
                  {t(actionType === 'delivery' ? 'Non Delivery Reason' : 'Non Pickup Reason')} <span className={classes.requiredAsterisk}>*</span>
                </label>
                <Select
                  showSearch
                  placeholder={t('Select reason')}
                  value={pieceData?.failure_reason}
                  onChange={(value) => handleReasonChange(value, record)}
                >
                  {availableReasons.map((reason) => (
                    <Select.Option key={reason.id} value={reason.name}>
                      {reason.name}
                    </Select.Option>
                  ))}
                </Select>
              </div>
            )}
            <div className={classes.commentContainer}>
              <label className={classes.reasonLabel}>
                {t('Comment')}
              </label>
              <Input
                placeholder={t('Add additional details...')}
                value={pieceData?.comment}
                maxLength={100}
                onChange={(e) => handleReasonCommentChange(e, record)}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderErrorModal = () => {
    return (
      <InvoiceUploadErrorModal
        isVisible={errorModalProps.isVisible}
        failureReason={errorModalProps.failureReason}
        handleOk={async () => {
          setErrorModalProps({
            isVisible: false,
            failureReason: '',
          });

          if (isPODForConsignment()) {
            const updatedPieceActions = { ...pieceLevelActions };
            consignmentData?.pieces_detail?.forEach((piece) => {
              updatedPieceActions[piece.reference_number] = {
                ...pieceLevelActions[piece.reference_number],
                pod: podUploadModalParams?.uploadedImageList,
              };
            });
            setPieceLevelActions(updatedPieceActions);
          } else {
            setPieceLevelActions({
              ...pieceLevelActions,
              [podUploadModalParams.referenceNumber]: {
                ...pieceLevelActions[podUploadModalParams.referenceNumber],
                pod: podUploadModalParams?.uploadedImageList,
              },
            });
          }
          setPODUploadModalParams({
            isVisible: false,
            isUploading: false,
            pieceStatusData: {},
          });
        }}
        handleCancel={() => {
          setPODUploadModalParams({
            ...podUploadModalParams,
            isUploading: false,
          });
          setErrorModalProps({
            isVisible: false,
            failureReason: '',
          });
        }}
      />
    );
  };

  const beforeUpload = (file) => {
    if (!file?.size) {
      return true;
    }
    const isLt1M = file?.size / 1024 / 1024 < 1;
    if (!isLt1M) {
      message.error(t('File must be smaller than 1 MB'));
    }
    return isLt1M;
  };

  const renderPODUploadModal = () => {
    return (
      <PodUpload
        isVisible={podUploadModalParams.isVisible}
        handleSubmit={handlePODValidation}
        title={t('Upload POD')}
        loading={podUploadModalParams.isUploading}
        listType="picture-card"
        uploadedImageList={podUploadModalParams.uploadedImageList}
        shouldSendExistingImages
        onModalClose={() => {
          setPODUploadModalParams({
            isVisible: false,
            pieceStatusData: {},
          });
        }}
        beforeUpload={beforeUpload}
        pieceStatusData={podUploadModalParams.pieceStatusData}
        consignmentData={consignmentData}
      />
    );
  };

  const handleQuantityChange = (value, record, fieldName) => {
    if (value < 1) {
      message.error(t('Quantity must be at least 1'));
      return;
    }
    if (value > record.quantity) {
      message.error(t('Quantity cannot be more than total quantity'));
      return;
    }
    setPieceLevelActions({
      ...pieceLevelActions,
      [record.reference_number]: {
        ...pieceLevelActions[record.reference_number],
        [fieldName]: value,
      },
    });
  };

  const renderPODUpload = (record) => {
    const proofImageList = pieceLevelActions?.[record.reference_number]?.pod;
    const isDisabled = record?.['_internal']?.[proofImageListKey]?.length;
    const minPodImages = getMinPODImages(record);
    const isUploadDisabled = isDisabled ||
      !pieceLevelActions?.[record.reference_number]?.status ||
      minPodImages === 0;

    return (
      <div>
        {(proofImageList?.length || record?.['_internal']?.[proofImageListKey]?.length) ? (
          <div style={{ display: 'flex' }}>
            <EyeOutlined
              className={classes.uploadIcon}
              onClick={() => setImageModalPreviewProps({
                isVisible: true,
                imageList: proofImageList || [],
              })}
              style={{ marginRight: '8px' }}
            />
            {!record?.['_internal']?.[proofImageListKey]?.length && <EditOutlined
              className={isDisabled ? classes.uploadIconDisabled : classes.uploadIcon}
              onClick={() => {
                if (isDisabled) return;
                setPODUploadModalParams({
                  isVisible: true,
                  referenceNumber: record.reference_number,
                  uploadedImageList: proofImageList,
                  pieceStatusData: {
                    status: pieceLevelActions?.[record.reference_number]?.status,
                    type: actionType,
                  },
                });
              }}
            />}
          </div>
        ) :
          <UploadOutlined
            className={isUploadDisabled ? classes.uploadIconDisabled : classes.uploadIcon}
            onClick={() => {
              if (isUploadDisabled) return;
              setPODUploadModalParams({
                isVisible: true,
                referenceNumber: record.reference_number,
                pieceStatusData: {
                  status: pieceLevelActions?.[record.reference_number]?.status,
                  type: actionType,
                },
                });
              }}
          />}
      </div>
    );
  };

  const renderEventTimestamp = () => {
    return (
      <div style={{ display: 'flex' }}>
        <DatePicker
          format="YYYY-MM-DD"
          onChange={(date, dateString) => setEventTimestamp({
            date: dateString,
          })}
          placeholder={t('Select')}
          className={classes.dateSelect}
          disabled={consignmentData?.delivery_completion_time}
          disabledDate={(current) => current.isAfter(moment())}
          value={deliveryAcknowledgement.eventTimestamp?.date ?
            moment(deliveryAcknowledgement.eventTimestamp?.date) : null}
        />
        <TimePicker
          format="HH:mm"
          onChange={(time, timeString) => setEventTimestamp({
            time: timeString,
          })}
          placeholder={t('Select')}
          disabled={consignmentData?.delivery_completion_time}
          className={classes.timeSelect}
          value={deliveryAcknowledgement.eventTimestamp?.time ?
            moment(deliveryAcknowledgement.eventTimestamp?.time, 'HH:mm') : null}
        />
      </div>
    );
  };

  const generateColumnsToShow = () => {
    const columnData: {
      title: string;
      dataIndex: string;
      key: string;
      width: number;
    }[] = [];

    const columnList = PieceDetailsColumnList(actionType);

    columnList?.forEach((item) => {
      const { column_id } = item;
      const itemToPush = {
        title: t(item.pretty_name),
        dataIndex: column_id,
        key: column_id,
        width: item.width ?? 150,
        fixed: item.fixed,
      };
      // when action type is pickup, hide quantity delivered column and vice versa
      if ((actionType === 'pickup' && column_id === 'quantity_delivered') || (actionType === 'delivery' && column_id === 'quantity_pickedup')) {
        return;
      }
      if (column_id === 'event_timestamp' && hideTimestamp) {
        return;
      }

      switch (column_id) {
        case 'serial_number':
          itemToPush['render'] = (_, __, index) => index + 1;
          break;
        case 'status':
          itemToPush['render'] = (_, record) => renderStatusDropdown(record);
          break;
        case 'pod':
          itemToPush['render'] = (_, record, index) => {
            if (isPODForConsignment() && index !== 0) {
              return null;
            }
            return renderPODUpload(record);
          };
          break;
        case 'event_timestamp':
          itemToPush['render'] = (_, record, index) => (index === 0 ? renderEventTimestamp() : null);
          break;
        case 'quantity_delivered':
          itemToPush['render'] = (_, record) => {
            const status = pieceLevelActions?.[record.reference_number]?.status;
            if (status === 'attempted') {
              return null;
            }
            return <InputNumber
              min={1}
              style={{ width: 100 }}
              disabled={actionType !== 'delivery' || !separateChildCNs}
              value={pieceLevelActions?.[record.reference_number]?.quantity_delivered
                ?? record.quantity}
              onChange={(e) => handleQuantityChange(e, record, 'quantity_delivered')}
            />;
          };
          break;
        case 'quantity_pickedup':
          itemToPush['render'] = (_, record) => {
            const status = pieceLevelActions?.[record.reference_number]?.status;
            if (status === 'not_picked_up') {
              return null;
            }
            return <InputNumber
              min={1}
              style={{ width: 100 }}
              disabled={actionType !== 'pickup' || !separateChildCNs}
              value={pieceLevelActions?.[record.reference_number]?.quantity_pickedup
                ?? record.quantity}
              onChange={(e) => handleQuantityChange(e, record, 'quantity_pickedup')}
            />;
          };
          break;
        default:
          itemToPush['render'] = (_, record) => _;
          break;
      }
      columnData.push(itemToPush);
    });
    return columnData;
  };

  const renderTable = () => {
    return (
      <Table
        rowKey={(record) => record['reference_number']}
        dataSource={consignmentData?.pieces_detail}
        columns={generateColumnsToShow()}
        rowSelection={rowSelection}
        scroll={{ x: 400 }}
        pagination={false}
        className={classes.tableParent}
      />
    );
  };

  const handleActionClick = (actionType, key?) => {
    if (actionType === 'selected' && !selectedRows?.length) {
      message.error(t('Please select tasks to perform this action'));
      return;
    }

    // When bulk action is undelivered and not picked up open failure reason modal
    if (key === 'attempted' || key === 'not_picked_up') {
      setNdrNprModalProps({
        isVisible: true,
        selectedStatus: key,
      });
      return;
    }

    const pieceLevelActionsData = {
      ...deliveryAcknowledgement.pieceLevelActions,
    };
    consignmentData?.pieces_detail?.forEach((piece) => {
      if (selectedRows?.includes(piece.reference_number)) {
        pieceLevelActionsData[piece.reference_number] = {
          ...pieceLevelActions[piece.reference_number],
          status: key,
          quantity_pickedup: ['not_picked_up'].includes(key) ? 0 : pieceLevelActions[piece.reference_number]?.quantity_pickedup || pieceLevelActions[piece.reference_number]?.quantity,
          quantity_delivered: ['attempted'].includes(key) ? 0 : pieceLevelActions[piece.reference_number]?.quantity_delivered || pieceLevelActions[piece.reference_number]?.quantity,
        };
      }
    });
    setPieceLevelActions(pieceLevelActionsData);
  };

  // use for opening ndr npr modal and updating piece details on confirm on bulk actions
  const handleNdrNprConfirm = async ({ reasonId, reasonName, comment }) => {
    const { selectedStatus } = ndrNprModalProps;

    const updatedPieceActions = { ...deliveryAcknowledgement.pieceLevelActions };

    consignmentData?.pieces_detail?.forEach((piece) => {
      if (selectedRows?.includes(piece.reference_number)) {
        updatedPieceActions[piece.reference_number] = {
          ...pieceLevelActions[piece.reference_number],
          status: selectedStatus,
          failure_reason: reasonName || '',
          failure_reason_id: reasonId || '',
          comment: comment || '',
          quantity_pickedup:
            selectedStatus === 'not_picked_up'
              ? 0
              : pieceLevelActions[piece.reference_number]?.quantity_pickedup ||
              pieceLevelActions[piece.reference_number]?.quantity,
          quantity_delivered:
            selectedStatus === 'attempted'
              ? 0
              : pieceLevelActions[piece.reference_number]?.quantity_delivered ||
              pieceLevelActions[piece.reference_number]?.quantity,
        };
      }
    });
    setPieceLevelActions(updatedPieceActions);
    setNdrNprModalProps({ isVisible: false, selectedStatus: '' });
  };

  const handleNdrNprCancel = () => {
    setNdrNprModalProps({ isVisible: false, selectedStatus: '' });
  };

  const bulkActionsMenu = (
    <Menu
      mode="vertical"
      selectable={false}
      style={{ width: '175px', borderRadius: '5px' }}
    >
      {BulkActionsMenu(actionType)?.map((action) => (
        <Menu.SubMenu
          title={t(action.label)}
          key={action.key}
        >
          {action.children?.map((child) => (
            <Menu.Item key={child.key} onClick={() => handleActionClick(action.key, child.key)}>
              {t(child.label)}
            </Menu.Item>
          ))}
        </Menu.SubMenu>
      ))}
    </Menu>
  );

  const renderBulkActionButton = () => {
    return (
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '12px' }}>
        <Dropdown overlay={bulkActionsMenu} trigger={['click']} disabled={isDisabled}>
          <Button className={classes.bulkActionsButton}>
            {t('Bulk Actions')} <DownOutlined />
          </Button>
        </Dropdown>
      </div>
    );
  };

  const renderData = () => {
    return (
      <div>
        {renderBulkActionButton()}
        {renderTable()}
        {podUploadModalParams?.isVisible && renderPODUploadModal()}
      </div>
    );
  };

  return (
    <Drawer
      className={classes.drawer}
      visible={isVisible}
      onClose={handleClose}
      placement="right"
      width={1400}
      closable={false}
      title={renderTitle()}
    >
      {renderData()}
      {imageModalPreviewProps.isVisible && renderImageModal()}
      {errorModalProps?.isVisible && renderErrorModal()}
      {ndrNprModalProps.isVisible && (
        <NdrNprReasonModal
          isVisible={ndrNprModalProps.isVisible}
          actionType={actionType}
          onCancel={handleNdrNprCancel}
          onConfirm={handleNdrNprConfirm}
          selectedRows={selectedRows}
        />
      )}
    </Drawer>
  );
};

const mapStateToProps = ({ masterData, deliveryAcknowledgement, vendor_config }) => {
  return {
    deliveryAcknowledgement,
    consignmentActionConfig: masterData?.vendor_config?.consignment_action_config || {},
    allFailureReasons: masterData?.all_failure_reasons || [],
    separateChildCNs: masterData?.separate_child_cns || false,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    setPieceLevelActions: (data) => dispatch(setPieceLevelActions(data)),
    markCNsAsDelivered: () => dispatch(markCNsAsDelivered()),
    markCNsAsPickedUp: () => dispatch(markCNsAsPickedUp()),
    setEventTimestamp: (data) => dispatch(setEventTimestamp(data)),
  };
};

const hocConfig: HocOptions = {
  connectRedux: {
    useRedux: true,
    mapStateToProps,
    mapDispatchToProps,
  },
  connectJss: {
    useJss: true,
    styleSheet: EditPieceDetailsSideDrawerStyles,
  },
  connectRouter: true,
  connectTranslession: true,
};

export default GenericHoc(hocConfig)(EditPieceDetailsSideDrawer);
